import Vue from "vue";
import axios from "axios";
import { simulatePostResponse } from "../mock-premises-data";

const state = {
  objects: [],
  mapObjects: [],
  multiSelectFilters: [],
  rangeFilters: [],
  filtersApplied: [],
  loading: false,
  // New server-side state
  filters: [],
  sorting: null,
  pagination: null,
  totalCount: 0,
  currentPage: 1,
  displayCount: 0,
};

axios.interceptors.request.use(function (config) {
  state.loading = true;
  return config;
});
axios.interceptors.response.use(
  function (response) {
    state.loading = false;
    return response;
  },
  function (error) {
    state.loading = false;
    console.log(error);
  }
);

const getters = {
  objects: (state) => {
    return state.objects;
  },
  loading: (state) => {
    return state.loading;
  },
  filters: (state) => {
    return state.filters;
  },
  sorting: (state) => {
    return state.sorting;
  },
  pagination: (state) => {
    return state.pagination;
  },
  totalCount: (state) => {
    return state.totalCount;
  },
  currentPage: (state) => {
    return state.currentPage;
  },
  displayCount: (state) => {
    return state.displayCount;
  },
  filtersApplied: (state) => {
    let filters = [];

    state.multiSelectFilters.filter((item) => {
      let filter = {
        name: item.name,
        applied: false,
      };
      filters.push(filter);
    });

    state.rangeFilters.filter((item) => {
      let filter = {
        name: item.name,
        applied: false,
      };
      filters.push(filter);
    });

    state.filtersApplied = filters;
    return state.filtersApplied;
  },
  multiSelectFilters: (state) => {
    return state.multiSelectFilters;
  },
  rangeFilters: (state) => {
    return state.rangeFilters;
  },
  currentObjects: (state, getters) => {
    // With server-side filtering, just return what we have
    return state.objects;
  },
  mapObjects: (state) => state.mapObjects,
};

const actions = {
  /**
   * Build the POST payload from current filter/sort/pagination state
   */
  buildPostPayload(
    { state },
    {
      page,
      sortingId,
      locationIds,
      districtIds,
      premiseTypes,
      areaMin,
      areaMax,
      minNumberOfWorkplaces,
      maxNumberOfWorkplaces,
    }
  ) {
    const payload = {
      page: page || state.currentPage || 1,
    };

    if (sortingId) {
      payload.sorting = sortingId;
    }

    // Location filters
    if (locationIds && locationIds.length > 0) {
      payload.location = locationIds;
    }

    if (districtIds && districtIds.length > 0) {
      payload.district = districtIds;
    }

    // Premise type filters
    if (premiseTypes && premiseTypes.length > 0) {
      payload.premisetype = premiseTypes;
    }

    // Area range
    if (areaMin !== undefined && areaMin !== "" && areaMin !== null) {
      payload.areaMin = parseInt(areaMin);
    }
    if (areaMax !== undefined && areaMax !== "" && areaMax !== null) {
      payload.areaMax = parseInt(areaMax);
    }

    if (
      minNumberOfWorkplaces !== undefined &&
      minNumberOfWorkplaces !== "" &&
      minNumberOfWorkplaces !== null
    ) {
      payload.minNumberOfWorkplaces = parseInt(minNumberOfWorkplaces);
    }
    if (
      maxNumberOfWorkplaces !== undefined &&
      maxNumberOfWorkplaces !== "" &&
      maxNumberOfWorkplaces !== null
    ) {
      payload.maxNumberOfWorkplaces = parseInt(maxNumberOfWorkplaces);
    }

    return payload;
  },

  /**
   * Process a server-like response object (real or mocked) and commit to store.
   * Shared by both fetchObjectsPost and fetchObjectsMock.
   */
  processResponse(
    { commit, dispatch, state },
    { responseData, append, mapOnly }
  ) {
    const data = responseData.Premises || responseData.premises || responseData;

    // Extract premises
    const premises = data.Premises || data.premises || [];

    // Map premises to internal format
    const mappedObjects = premises.map((item, index) => {
      // Generate mock images only if object has no images
      const generateMockImages = () => {
        const imageCount = (index % 3) + 2; // 2-4 images per object
        const images = [];
        for (let i = 0; i < imageCount; i++) {
          images.push({
            imgSrc: `https://picsum.photos/1020/800?random=${
              index * 10 + i + 100
            }`,
            altText: `${item.Title || item.title || "Property"} - image ${
              i + 1
            }`,
          });
        }
        return images;
      };

      const images = item.Images || item.images || [];
      return {
        url: item.Url || item.url || "/",
        image: item.Image || item.image || null,
        images: images.length > 0 ? images : generateMockImages(),
        title: item.Title || item.title || "",
        sizeFrom: item.SizeFrom || item.sizeFrom || 0,
        sizeTo: item.SizeTo || item.sizeTo || 0,
        city: item.City || item.city || "",
        district: item.District || item.district || "",
        premiseTypes:
          item.PremiseTypes ||
          item.premiseTypes ||
          item.PremiseType ||
          item.premiseType ||
          [],
        address: item.Address || item.address || "",
        selectedFeatures: (
          item.SelectedFeatures ||
          item.selectedFeatures ||
          []
        ).map((f) => ({
          description: f.Description || f.description || "",
          icon: f.Icon || f.icon || "",
        })),
        overflowFeaturesCount:
          item.OverflowFeaturesCount || item.overflowFeaturesCount || 0,
        numberOfWorkplaces:
          item.NumberOfWorkplaces || item.numberOfWorkplaces || "",
        minNumberOfWorkplaces:
          item.MinNumberOfWorkplaces || item.minNumberOfWorkplaces || 0,
        maxNumberOfWorkplaces:
          item.MaxNumberOfWorkplaces || item.maxNumberOfWorkplaces || 0,
        changedDate: item.ChangedDate || item.changedDate || "",
        latitude: item.Latitude || item.latitude || 0,
        longitude: item.Longitude || item.longitude || 0,
        pricePerSquare: item.PricePerSqure || item.pricePerSquare || null,
        textRow: item.TextRow || item.textRow || "",
        textRow2: item.TextRow2 || item.textRow2 || "",
      };
    });

    if (mapOnly) {
      commit("setMapObjects", mappedObjects);
      return;
    }
    if (append) {
      commit("appendObjects", mappedObjects);
    } else {
      commit("setObjects", mappedObjects);
    }

    // Extract and store filters
    const premisesFilters = data.PremisesFilters || data.premisesFilters || [];
    if (premisesFilters.length > 0) {
      commit("setFilters", premisesFilters);
      dispatch("setupFilters", premisesFilters);
    }

    // Extract sorting options
    const sorting = data.Sorting || data.sorting || null;
    if (sorting) {
      commit("setSorting", sorting);
    }

    // Extract pagination
    const pagination = data.Pagination || data.pagination || null;
    if (pagination) {
      commit("setPagination", pagination);
    }

    // Extract total count
    const totalCount = data.TotalCount || data.totalCount || 0;
    commit("setTotalCount", totalCount);

    // Update display count
    const display = pagination
      ? pagination.display || pagination.Display || 0
      : 0;
    if (append) {
      commit("setDisplayCount", state.objects.length);
    } else {
      commit("setDisplayCount", display || mappedObjects.length);
    }

    // Update current page
    const currentPage = pagination
      ? pagination.currentPage || pagination.CurrentPage || 1
      : 1;
    commit("setCurrentPage", currentPage);
  },

  /**
   * Fetch objects using mock data (no real HTTP call).
   * Simulates server-side filtering, sorting, and pagination entirely client-side.
   */
  fetchObjectsMock(
    { dispatch, state },
    { payload, append, mockData, mapOnly }
  ) {
    return new Promise((resolve) => {
      state.loading = true;

      // Simulate a small network delay for realism
      setTimeout(() => {
        let responseData;

        if (mockData) {
          const parsedMockData =
            typeof mockData === "string" ? JSON.parse(mockData) : mockData;

          responseData = {
            Premises: {
              PremisesFilters:
                parsedMockData.PremisesFilters ||
                parsedMockData.premisesFilters ||
                [],
              Premises:
                parsedMockData.Premises || parsedMockData.premises || [],
              TotalCount:
                parsedMockData.TotalCount ||
                parsedMockData.totalCount ||
                (parsedMockData.Premises || parsedMockData.premises || [])
                  .length,
              Sorting: parsedMockData.Sorting || parsedMockData.sorting || null,
              Pagination: parsedMockData.Pagination ||
                parsedMockData.pagination || {
                  display: (
                    parsedMockData.Premises ||
                    parsedMockData.premises ||
                    []
                  ).length,
                  total:
                    parsedMockData.TotalCount ||
                    parsedMockData.totalCount ||
                    (parsedMockData.Premises || parsedMockData.premises || [])
                      .length,
                  currentPage: 1,
                  pageCount: 1,
                },
            },
          };
        } else {
          responseData = simulatePostResponse(payload || { page: 1 });
        }

        dispatch("processResponse", {
          responseData: responseData,
          append: append || false,
          mapOnly: mapOnly || false,
        });

        state.loading = false;
        resolve();
      }, 200);
    });
  },

  /**
   * Fetch objects via POST. Used for initial load, filter changes, sorting changes.
   * Replaces the current objects list.
   */
  fetchObjectsPost({ commit, dispatch, state }, { apiUrl, payload, append }) {
    return new Promise((resolve) => {
      // POST to the current page URL
      const url = apiUrl || window.location.pathname;

      axios
        .post(url, payload || { page: 1 }, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        })
        .then((response) => {
          if (response && response.data) {
            dispatch("processResponse", {
              responseData: response.data,
              append: append || false,
            });

            state.loading = false;
            resolve();
          }
        })
        .catch((error) => {
          console.error("Error fetching objects:", error);
          state.loading = false;
          resolve();
        });
    });
  },

  /**
   * Load more objects (pagination - append to existing list)
   */
  loadMoreObjects({ state, dispatch }, { apiUrl, payload }) {
    const nextPage = (state.currentPage || 1) + 1;
    const pageCount = state.pagination
      ? state.pagination.pageCount || state.pagination.PageCount || 1
      : 1;

    if (nextPage > pageCount) {
      return Promise.resolve();
    }

    const mergedPayload = Object.assign({}, payload || {}, { page: nextPage });

    return dispatch("fetchObjectsPost", {
      apiUrl: apiUrl,
      payload: mergedPayload,
      append: true,
    });
  },

  /**
   * Fetch all objects (no pagination) for the map.
   * Sends limit: 999 so the backend returns every matching result.
   */
  fetchObjectsForMap({ dispatch }, { apiUrl, payload }) {
    return new Promise((resolve) => {
      const url = apiUrl || window.location.pathname;
      axios
        .post(url, Object.assign({}, payload, { page: 1, limit: 999 }), {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        })
        .then((response) => {
          if (response && response.data) {
            dispatch("processResponse", {
              responseData: response.data,
              mapOnly: true,
            });
          }
          resolve();
        })
        .catch((error) => {
          console.error("Error fetching map objects:", error);
          resolve();
        });
    });
  },

  /**
   * Legacy fetchObjects for backward compatibility
   * Now uses POST instead of GET
   */
  fetchObjects({ dispatch }, options) {
    return dispatch("fetchObjectsPost", {
      apiUrl: options.apiUrl || window.location.pathname,
      payload: { page: 1 },
      append: false,
    });
  },

  setObjects({ commit }, { objects }) {
    objects = objects.map((object) => {
      return object;
    });
    commit("setObjects", objects);
  },

  setMultiSelectFilter({ commit }, filter) {
    commit("setMultiSelectFilter", filter);
  },

  setRangeFilter({ commit }, filter) {
    commit("setRangeFilter", filter);
  },

  resetAllFilters({ commit }) {
    commit("resetAllFilters");
  },

  setupFilters({ commit }, premisesFilters) {
    // Clear existing filters first
    commit("clearMultiSelectFilters");
    commit("clearRangeFilters");

    let allMultiSelectRawOptions = premisesFilters.filter(
      (filter) => filter.Items || filter.items
    );

    if (allMultiSelectRawOptions) {
      allMultiSelectRawOptions.map((rawOptions) => {
        const items = rawOptions.Items || rawOptions.items;
        if (items) {
          let multiSelectFilter = {};
          multiSelectFilter.displayName =
            rawOptions.DisplayName || rawOptions.displayName;
          multiSelectFilter.name = (
            rawOptions.Name ||
            rawOptions.name ||
            ""
          ).toLowerCase();

          if (multiSelectFilter.name === "location") {
            let x = [];

            items.forEach((item) => {
              const cityName = item.Name || item.name;
              const cityId = item.Id || item.id;
              const cityActive = item.Active || item.active || false;
              const districts = item.Districts || item.districts || [];

              let groupLabel = {
                groupLabel: true,
                display: cityName,
                value: cityName,
                id: cityId,
                checked: cityActive,
              };
              x.push(groupLabel);

              districts.map((district) => {
                const districtName = district.Name || district.name;
                const districtId = district.Id || district.id;
                const districtActive =
                  district.Active || district.active || false;

                let districtItem = {
                  display: districtName,
                  value: `${districtName.toLowerCase()}-${cityName.toLowerCase()}`,
                  id: districtId,
                  checked: districtActive,
                  group: cityName,
                };

                x.push(districtItem);
              });
            });
            multiSelectFilter.items = x;
          } else if (multiSelectFilter.name === "premisetype") {
            multiSelectFilter.items = items.map((item) => {
              // Items can be strings or objects
              if (typeof item === "string") {
                return {
                  display: item,
                  value: item.toLowerCase(),
                  checked: false,
                };
              }
              return {
                display: item.Name || item.name || item,
                value: (item.Name || item.name || item).toLowerCase(),
                id: item.Id || item.id,
                checked: item.Active || item.active || false,
              };
            });
          } else {
            multiSelectFilter.items = items.map((item) => {
              if (typeof item === "string") {
                return {
                  display: item,
                  value: item.toLowerCase(),
                  checked: false,
                };
              }
              return {
                display: item.Name || item.name || item,
                value: (item.Name || item.name || item).toLowerCase(),
                checked: item.Active || item.active || false,
              };
            });
          }

          commit("addMultiSelectFilter", multiSelectFilter);
        }
      });
    }

    let allRangeOptions = premisesFilters.filter((filter) => {
      const maxVal = filter.MaxValue || filter.maxValue;
      return maxVal !== undefined;
    });

    if (allRangeOptions) {
      allRangeOptions.map((range) => {
        const normalizedRange = {
          name: (range.Name || range.name || "").toLowerCase(),
          displayName: range.DisplayName || range.displayName || "",
          minValue: range.MinValue || range.minValue || 0,
          maxValue: range.MaxValue || range.maxValue || 0,
          adjustedMax:
            range.AdjustedMax ||
            range.adjustedMax ||
            range.MaxValue ||
            range.maxValue ||
            0,
          selectedMin: range.MinValue || range.minValue || 0,
          selectedMax:
            range.AdjustedMax ||
            range.adjustedMax ||
            range.MaxValue ||
            range.maxValue ||
            0,
        };

        commit("addRangeFilter", normalizedRange);
      });
    }
  },
};

const mutations = {
  setObjects(state, objects) {
    Vue.set(state, "objects", objects);
  },
  setMapObjects(state, objects) {
    Vue.set(state, "mapObjects", objects);
  },
  appendObjects(state, objects) {
    const combined = state.objects.concat(objects);
    Vue.set(state, "objects", combined);
  },
  setFilters(state, filters) {
    Vue.set(state, "filters", filters);
  },
  setSorting(state, sorting) {
    Vue.set(state, "sorting", sorting);
  },
  setPagination(state, pagination) {
    Vue.set(state, "pagination", pagination);
  },
  setTotalCount(state, totalCount) {
    state.totalCount = totalCount;
  },
  setDisplayCount(state, count) {
    state.displayCount = count;
  },
  setCurrentPage(state, page) {
    state.currentPage = page;
  },
  clearMultiSelectFilters(state) {
    Vue.set(state, "multiSelectFilters", []);
  },
  clearRangeFilters(state) {
    Vue.set(state, "rangeFilters", []);
  },
  addMultiSelectFilter(state, filter) {
    state.multiSelectFilters.push(filter);
  },
  addRangeFilter(state, filter) {
    state.rangeFilters.push(filter);
  },
  setMultiSelectFilter(state, filter) {
    let filterToUpdate = state.multiSelectFilters.find(
      (msf) => msf.name == filter.id
    );

    let filterStatus = state.filtersApplied;

    filterStatus.filter((filter) => {
      if (filter.name === filterToUpdate.name) {
        filter.applied = true;
      }
    });

    if (filterToUpdate) {
      Vue.set(filterToUpdate, "items", filter.options);
      Vue.set(state, "filtersApplied", filterStatus);
    }
  },
  setRangeFilter(state, filter) {
    let filterToUpdate = state.rangeFilters.find(
      (rf) => rf.name === filter.name
    );

    let filterStatus = state.filtersApplied;

    filterStatus.filter((filter) => {
      if (filter.name === filterToUpdate.name) {
        filter.applied = true;
      }
    });

    if (filterToUpdate) {
      Vue.set(filterToUpdate, "selectedMin", filter.selectedMin);
      Vue.set(filterToUpdate, "selectedMax", filter.selectedMax);
      Vue.set(state, "filtersApplied", filterStatus);
    }
  },
  setAreaRange(state, range) {
    Vue.set(state, "areaRange", range);
  },
  resetAllFilters(state) {
    // Reset URL
    let url = window.location.toString();
    url = url.substring(0, url.indexOf("?"));
    window.history.pushState("", "", url);

    state.multiSelectFilters.map((filter) =>
      filter.items.map((item) => {
        Vue.set(item, "checked", false);
      })
    );
    state.rangeFilters.map((filter) => {
      filter.selectedMin = filter.minValue;
      filter.selectedMax = filter.adjustedMax;
    });

    state.filtersApplied.map((filter) => {
      filter.applied = false;
    });

    // Reset pagination state
    state.currentPage = 1;
    state.totalCount = 0;
    state.displayCount = 0;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
